import _ from 'lodash';
import { InputProps } from '@nerdwallet/currency';

import HomeAffordabilityDefaultInputs from '../../components/home-affordability-calculator/default-inputs';
import HomeAffordabilityInputLibrary from '../../components/home-affordability-calculator/input-library';
import MortgageDefaultInputs from '../../components/mortgage-calculator/shared/default-inputs';
import MortgageInputLibrary from '../../components/mortgage-calculator/shared/input-library';
import RefinanceDefaultInputs from '../../components/refinance-calculator/default-inputs';
import RefinanceInputLibrary from '../../components/refinance-calculator/input-library';
import RentVsBuyDefaultInputs from '../../components/rentvsbuy-calculator/control-panel/shared/default-inputs';
import RentVsBuyInputLibrary from '../../components/rentvsbuy-calculator/control-panel/shared/input-library';
import CostOfLivingInputLibrary from '../../components/cost-of-living-calculator/compare-cities/input-library';

enum HomeAffordabilityOverridesValues {
  fundsNeeded,
  zipCode, // this will be set onRouteEnter with geocoding services
}

enum MortgageOverridesValues {
  zipCode,
}

// analytics payload must have at least 1 key (legacy OR bedrock) OR both
export type TrackOnChangePayload = {
  bedrock?: any;
};

type Analytics = {
  enabled: boolean;
  payload: TrackOnChangePayload;
};

export type RefinanceCalculatorField =
  | keyof typeof RefinanceInputLibrary
  | keyof typeof RefinanceDefaultInputs;

export type HACalculatorField =
  | keyof typeof HomeAffordabilityInputLibrary
  | keyof typeof HomeAffordabilityOverridesValues
  | keyof typeof HomeAffordabilityDefaultInputs;

export type MortgageCalculatorField =
  | keyof typeof MortgageInputLibrary
  | keyof typeof MortgageOverridesValues
  | keyof ReturnType<typeof MortgageDefaultInputs>;

export type AmortizationCalculatorField = MortgageCalculatorField;

export type RentVsBuyCalculatorField =
  | keyof typeof RentVsBuyInputLibrary
  | keyof typeof RentVsBuyDefaultInputs;

export type CostOfLivingCalculatorField = keyof typeof CostOfLivingInputLibrary;

export type Field =
  | RefinanceCalculatorField
  | HACalculatorField
  | AmortizationCalculatorField
  | RentVsBuyCalculatorField
  | MortgageCalculatorField
  | CostOfLivingCalculatorField;

/**
 * Validations are taken from redux, it's an object gathering all validations from
 * inputs, where each key is a filed, and the value is an array of strings containing any error message
 */
export type Validations = Record<Field, string[]>;
export type ErrorMessage = string;

type HacFieldsReducerData = {
  hasLinkedCreditScore?: boolean;
  /**
   * All the input values from redux
   */
  overrides?: Record<HACalculatorField, any>;
  /**
   * All the input placeholders from redux
   */
  outputs?: Record<HACalculatorField, any>;
  adjustedOutputs?: Record<HACalculatorField, any>;
};

type MortgageFieldsReducerData = {
  outputs?: Record<string, any>;
};

type RefinanceFieldsReducerData = {
  outputs?: Record<string, any>;
};

type RentVsBuyFieldsReducerData = {
  outputs?: Record<string, any>;
};

/**
 * Creating this Interface for gathering all the necessary info from reducers
 */
export interface ReducerInputsData<T extends Field> {
  /**
   * All the input values from redux
   */
  manualInputs?: Record<T, any>;
  /**
   * All the input placeholders from redux
   */
  inputs?: Record<T, any>;
  /**
   * All the validation from fields, check `Validations` for structure
   */
  validations?: Validations;
}

export type BaseInputProps = Pick<
  InputProps,
  | 'label'
  | 'tooltip'
  | 'fullWidth'
  | 'className'
  | 'onBlur'
  | 'value'
  | 'placeholder'
  | 'error'
  | 'errorMessage'
>;

/**
 * Interface to gather other props for Inputs
 */
export type InputOtherProps = BaseInputProps & {
  /**
   * For dispatching any action
   */
  dispatch?: any;
  /**
   * Analytics payload that will be triggered onChange event. It will gather legacy & bedrock payload
   * Check `Analytics` type for further information on the structure
   */
  analytics?: Analytics;
  /**
   * The same input can be shown differently depending on where its being shown:
   *  - TEXT - NumberInput (e.g: Onboarding Modal)
   *  - RANGE - Number Stepper (e.g: Control Panel)
   */
  inputType?: keyof typeof INPUT_TRACKING_TYPES;
};

/**
 * Interface that describes all props for all Calculator inputs that are used
 * on the OnboardingModal & ControlPanel
 */
type CalculatorInput<T extends Field> = ReducerInputsData<T>;

export type RefinanceInput = CalculatorInput<RefinanceCalculatorField> &
  RefinanceFieldsReducerData &
  InputOtherProps;

export type HomeAffordabilityInput = CalculatorInput<HACalculatorField> &
  HacFieldsReducerData &
  InputOtherProps;

export type MortgageInput = CalculatorInput<MortgageCalculatorField> &
  MortgageFieldsReducerData &
  InputOtherProps;

export type AmortizationInput = CalculatorInput<MortgageCalculatorField> &
  MortgageFieldsReducerData &
  InputOtherProps;

export type RentVsBuyInput = CalculatorInput<RentVsBuyCalculatorField> &
  RentVsBuyFieldsReducerData &
  InputOtherProps;

export type CostOfLivingInput = CalculatorInput<CostOfLivingCalculatorField> &
  InputOtherProps;

export const REFINANCE_GOAL_ANALYTICS_HASH_VALUE = {
  monthly_payments: 'LOWER_MONTHLY_PAYMENT',
  total_mortgage_interest: 'LOWER_MORTGAGE_INTEREST_RATE',
};

export const LOAN_TERM_ANALYTICS_HASH_VALUE = {
  30: 'LOAN_TERM_30_YEAR',
  15: 'LOAN_TERM_15_YEAR',
};

export const CREDIT_SCORE_ANALYTICS_HASH_VALUE = {
  excellent: 'EXCELLENT_720_850',
  '720-850': 'EXCELLENT_720_850',
  good: 'GOOD_690_719',
  '690-719': 'GOOD_690_719',
  average: 'AVERAGE_630_689',
  '630-689': 'AVERAGE_630_689',
  belowAvg: 'BELOW_AVERAGE_580_629',
  poor: 'POOR_350_629',
  '350-629': 'POOR_350_629',
};

export function hasNoErrors(validations: Validations): boolean {
  if (_.isEmpty(validations)) return true;

  // will search for an Field with no empty error, as we could have some Fields with an empty array
  const result = _.find(validations, (fieldValidations) => {
    return !_.isEmpty(fieldValidations);
  });

  return _.isEmpty(result);
}

export const INPUT_TRACKING_TYPES = {
  TEXT: 'TEXT',
  RANGE: 'RANGE',
};
